import type { IControlOption, IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import TabSelect from '@/components/TabSelect';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import type { GetOptionDisabled } from '@/types/Controls/TabSelect.types';
import {
  Fade,
  type OutlinedInputProps,
  Popover,
  type PopoverOrigin,
  type PopoverProps,
  type SxProps,
  type Theme,
  backdropClasses,
  popoverClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  type MouseEventHandler,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

const StyledPopover = styled(Popover, { shouldForwardProp: prop => prop !== 'dimBackground' })<
  PopoverProps & { dimBackground?: boolean }
>({
  [`& .${popoverClasses.paper}`]: {
    borderRadius: '8px',
    backgroundColor: 'white',
    border: 'none',
    boxShadow: '0 10px 10px 0 rgb(16 10 85 / .1)',
    display: 'flex',
    flexGrow: 1,
  },
  variants: [
    {
      props: ({ dimBackground }) => dimBackground,
      style: {
        [`& .${backdropClasses.root}`]: {
          backgroundColor: 'rgb(0 0 0 / .4)',
        },
      },
    },
  ],
});

interface IProps<T> {
  renderInput?: (props: OutlinedInputProps) => ReactNode;
  confirmLabel?: string;
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
  getOptionDisabled?: GetOptionDisabled<T>;
  grouped?: boolean;
  label: string;
  closeOnClickOutside?: boolean;
  dimBackground?: boolean;
  sx?: SxProps<Theme>;
  tabSelectSx?: SxProps<Theme>;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  withToggle?: boolean;
  disabled?: boolean;
  withAllOption?: boolean;
}

function SearchSelect<T extends IControlOption<any, any> = IControlOption | IGroupedControlOption>({
  renderInput,
  confirmLabel,
  options = [],
  value = [],
  onChange,
  getOptionDisabled,
  grouped,
  label,
  closeOnClickOutside = false,
  dimBackground = true,
  sx,
  tabSelectSx,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  withToggle = false,
  disabled,
  withAllOption,
}: IProps<T>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [pendingValue, setPendingValue] = useState<T[]>(value);

  const updatePendingValue = useCallback((updatedValues: T[]) => {
    setPendingValue(updatedValues ?? []);
  }, []);

  const resetPendingValues = useCallback(() => {
    setPendingValue(value);
  }, [value]);

  useEffect(() => {
    if (!value?.length) {
      setPendingValue([]);
      return;
    }
    setPendingValue(value);
  }, [value]);

  const handleOpen = useCallback<MouseEventHandler<HTMLDivElement>>(
    event => {
      if (disabled) {
        return;
      }
      resetPendingValues();
      setAnchorEl(event.currentTarget);
    },
    [disabled, resetPendingValues],
  );

  const onClose = useCallback(() => {
    resetPendingValues();
    setAnchorEl(null);
  }, [resetPendingValues]);

  const handleClose = useCallback<(event: SyntheticEvent | Event, reason: string) => void>(
    (_, reason) => {
      if (reason && !closeOnClickOutside) return;

      onClose();
    },
    [onClose, closeOnClickOutside],
  );

  const handleSave = useCallback(() => {
    if (areArraysOfObjectsEqual(value, pendingValue)) {
      onClose();
      return;
    }
    onChange(pendingValue);
    onClose();
  }, [onChange, onClose, value, pendingValue]);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const popoverId = useMemo(() => (open ? 'column-select' : undefined), [open]);

  const tabSelectProps = useMemo(
    () => ({
      label,
      options,
      grouped,
      confirmLabel,
      onChange: updatePendingValue,
      onClose: handleClose,
      onSubmit: handleSave,
      getOptionDisabled,
      formGroupSx: {
        maxWidth: 'none',
        ...tabSelectSx,
      },
      withContainsControl: withToggle,
      withAllOption,
    }),
    [
      label,
      options,
      grouped,
      confirmLabel,
      tabSelectSx,
      updatePendingValue,
      handleSave,
      handleClose,
      withToggle,
      withAllOption,
      getOptionDisabled,
    ],
  );

  return (
    <>
      {renderInput?.({ onClick: handleOpen })}
      <StyledPopover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slots={{ transition: Fade }}
        dimBackground={dimBackground}
        sx={sx}
      >
        <div className='w-full'>
          <TabSelect value={pendingValue} {...tabSelectProps} />
        </div>
      </StyledPopover>
    </>
  );
}

export default SearchSelect;
